import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, Stack, Switch, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import { Refresh, SwapHoriz, Warning } from '@mui/icons-material';
import { orderProcessorAPI } from '../../services/api';
import { errMsg, fmtDateTime, isoDaysAgo, num, StatusChip } from './common';
import CustomerOrderDialog from './CustomerOrderDialog';
import NextDistributorDialog from './NextDistributorDialog';

type View = 'optimizer' | 'customer';
type SortKey = 'medicine' | 'pack' | 'run' | 'po' | 'distributor' | 'optimizer' | 'customer' | 'order' | 'received' | 'line' | 'po_status' | 'sent';

interface Props {
  view: View;
  storeId?: number | null;
  onOpenPo: (poId: number) => void;
}

/** Numeric part of "PO-2026-0083" / "OPR-2026-0008" so 10000 sorts after 9999 (text sort would not). */
const docNo = (v: any) => (v ? Number(String(v).split('-').pop()) : null);
const nOrNull = (v: any) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const SORT_VALUE: Record<SortKey, (l: any) => string | number | null> = {
  medicine: (l) => String(l.medicine_name || '').toLowerCase(),
  pack: (l) => (l.pack_size ? String(l.pack_size).toLowerCase() : null),
  run: (l) => docNo(l.run_number),
  po: (l) => docNo(l.po_number),
  distributor: (l) => String(l.distributor_name || '').toLowerCase(),
  optimizer: (l) => (l.source_type !== 'customer' ? nOrNull(l.optimizer_qty) : null),
  customer: (l) => (l.source_type !== 'optimizer' ? nOrNull(l.customer_qty_units) : null),
  order: (l) => nOrNull(l.loose_qty),
  received: (l) => nOrNull(l.received_qty),
  line: (l) => l.line_status || null,
  po_status: (l) => l.po_status || null,
  sent: (l) => (l.sent_at ? String(l.sent_at) : null),
};

/**
 * Purchase-order lines seen from their source: the optimizer list, or customer (commitment) orders.
 * Every line shows its distributor, PO and status; customer lines link to the full customer order.
 */
const PoLinesView: React.FC<Props> = ({ view, storeId, onOpenPo }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [from, setFrom] = useState(isoDaysAgo(90));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [storeOnly, setStoreOnly] = useState(true);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [poFilter, setPoFilter] = useState<string | null>(null);
  const [distFilter, setDistFilter] = useState<string | null>(null);
  const [runFilter, setRunFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [moveLine, setMoveLine] = useState<any | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setRows(await orderProcessorAPI.listPoLines({
        view, from, to, status: status || undefined, include_cancelled: includeCancelled,
        store_id: storeOnly && storeId ? storeId : undefined,
      }));
      setPage(0);
    } catch (e) { setError(errMsg(e)); } finally { setLoading(false); }
  }, [view, from, to, status, includeCancelled, storeOnly, storeId]);

  useEffect(() => { load(); }, [load]);

  // PO / distributor choices come from the lines currently loaded
  const poOptions = useMemo(() => {
    const m = new Map<string, any>();
    rows.filter((l) => !runFilter || l.run_number === runFilter).forEach((l) => { if (!m.has(l.po_number)) m.set(l.po_number, { po: l.po_number, distributor: l.distributor_name, status: l.po_status, n: 0 }); m.get(l.po_number).n += 1; });
    return Array.from(m.values()).sort((a, b) => (docNo(b.po) || 0) - (docNo(a.po) || 0));
  }, [rows, runFilter]);
  const runOptions = useMemo(() => {
    const m = new Map<string, any>();
    rows.forEach((l) => {
      if (!l.run_number) return;
      if (!m.has(l.run_number)) m.set(l.run_number, { run: l.run_number, store: l.store_name, source: l.run_source, n: 0 });
      m.get(l.run_number).n += 1;
    });
    return Array.from(m.values()).sort((a, b) => (docNo(b.run) || 0) - (docNo(a.run) || 0));
  }, [rows]);
  const distOptions = useMemo(() => Array.from(new Set(rows.map((l) => l.distributor_name).filter(Boolean))).sort() as string[], [rows]);

  // Search also accepts short PO numbers: "83", "0083", "po-83" all find PO-2026-0083
  const matchesSearch = (l: any) => {
    if (!search) return true;
    const q = search.toLowerCase().trim();
    const hay = `${l.medicine_name} ${l.manufacturer_name} ${l.distributor_name} ${l.po_number} ${l.run_number} ${(l.customer_orders || []).map((o: any) => `#${o.id} ${o.id} ${o.customer_name}`).join(' ')}`.toLowerCase();
    if (hay.includes(q)) return true;
    if (/^opr/.test(q)) {
      // "OPR-2026-0008", "opr-8", "opr 0008" → run number ending 0008
      const last = (q.match(/\d+/g) || []).pop();
      return !!last && Number(String(l.run_number || '').split('-').pop()) === Number(last);
    }
    const digits = q.replace(/^po-?/, '').replace(/\D/g, '');
    return !!digits && /^\d+$/.test(q.replace(/^po-?/, '')) && Number(String(l.po_number).split('-').pop()) === Number(digits);
  };

  const filtered = useMemo(() => rows.filter((l) =>
    matchesSearch(l)
    && (!runFilter || l.run_number === runFilter)
    && (!poFilter || l.po_number === poFilter)
    && (!distFilter || l.distributor_name === distFilter)
    && (!onlyFlagged || l.open_duplicates?.length || l.short_of_customer)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rows, search, onlyFlagged, poFilter, distFilter, runFilter]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const get = SORT_VALUE[sort.key]; const mul = sort.dir === 'asc' ? 1 : -1;
    return filtered.map((r, idx) => ({ r, idx, v: get(r) })).sort((a, b) => {
      if (a.v === null && b.v === null) return a.idx - b.idx;
      if (a.v === null) return 1;
      if (b.v === null) return -1;
      return a.v < b.v ? -mul : a.v > b.v ? mul : a.idx - b.idx;
    }).map((x) => x.r);
  }, [filtered, sort]);
  const pageRows = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((l) => { c[l.line_status] = (c[l.line_status] || 0) + 1; });
    return c;
  }, [rows]);
  const dupCount = rows.filter((l) => l.open_duplicates?.length).length;
  const shortCount = rows.filter((l) => l.short_of_customer).length;

  const toggleSort = (key: SortKey) => { setSort((cur) => (cur && cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })); setPage(0); };
  const Head: React.FC<{ k: SortKey; label: React.ReactNode; align?: 'right' }> = ({ k, label, align }) => (
    <TableCell align={align} sortDirection={sort?.key === k ? sort.dir : false}>
      <TableSortLabel active={sort?.key === k} direction={sort?.key === k ? sort.dir : 'asc'} onClick={() => toggleSort(k)}>{label}</TableSortLabel>
    </TableCell>
  );

  const flags = (l: any) => (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.3 }}>
      {view === 'optimizer' && l.source_type === 'both' && <Chip size="small" color="info" variant="outlined" label="+ customer order" />}
      {view === 'customer' && l.source_type === 'both' && <Chip size="small" color="info" variant="outlined" label="+ optimizer" />}
      {l.open_duplicates?.length > 0 && (
        <Tooltip title="The same item for the same store is also on another open PO — possible double order">
          <Chip size="small" color="warning" icon={<Warning />} label={`also on ${l.open_duplicates.join(', ')}`} />
        </Tooltip>
      )}
      {l.short_of_customer && (
        <Tooltip title={`Customers ordered ${num(l.customer_qty_units, 0)} units but this PO line orders ${num(l.loose_qty, 0)}`}>
          <Chip size="small" color="error" icon={<Warning />} label="PO below customer qty" />
        </Tooltip>
      )}
      {l.reorder_of_po_number && <Chip size="small" variant="outlined" label={`re-order of ${l.reorder_of_po_number}`} />}
      {l.reordered_to_po_number && <Chip size="small" color="warning" label={`not available here → moved to ${l.reordered_to_po_number}`} />}
    </Stack>
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1.5 }} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
        <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open (not yet received)</MenuItem>
            <MenuItem value="received">Received</MenuItem>
            <MenuItem value="partial">Partial</MenuItem>
            <MenuItem value="not_supplied">Not supplied</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete size="small" sx={{ minWidth: 220 }} options={runOptions} value={runOptions.find((o) => o.run === runFilter) || null}
          getOptionLabel={(o: any) => `${o.run} · ${o.store} (${o.n})`}
          isOptionEqualToValue={(a: any, b: any) => a.run === b.run}
          onChange={(_, v: any) => { setRunFilter(v ? v.run : null); setPoFilter(null); setPage(0); }}
          renderOption={(props, o: any) => (
            <li {...props} key={o.run}>
              <Box><Typography variant="body2"><b>{o.run}</b> · {o.store}</Typography>
                <Typography variant="caption" color="text.secondary">{o.source} run · {o.n} line(s)</Typography></Box>
            </li>
          )}
          renderInput={(p) => <TextField {...p} label="Run" placeholder="All runs" />} />
        <Autocomplete size="small" sx={{ minWidth: 260 }} options={poOptions} value={poOptions.find((o) => o.po === poFilter) || null}
          getOptionLabel={(o: any) => `${o.po} · ${o.distributor} (${o.n})`}
          isOptionEqualToValue={(a: any, b: any) => a.po === b.po}
          onChange={(_, v: any) => { setPoFilter(v ? v.po : null); setPage(0); }}
          renderOption={(props, o: any) => (
            <li {...props} key={o.po}>
              <Box><Typography variant="body2"><b>{o.po}</b> · {o.distributor}</Typography>
                <Typography variant="caption" color="text.secondary">{o.n} line(s) · {String(o.status).replace(/_/g, ' ')}</Typography></Box>
            </li>
          )}
          renderInput={(p) => <TextField {...p} label="PO" placeholder="All POs" />} />
        <Autocomplete size="small" sx={{ minWidth: 220 }} options={distOptions} value={distFilter}
          onChange={(_, v) => { setDistFilter(v); setPage(0); }}
          renderInput={(p) => <TextField {...p} label="Distributor" placeholder="All distributors" />} />
        <TextField size="small" label={view === 'customer' ? 'Search medicine / PO / order # / customer' : 'Search medicine / PO'}
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: 280 }} />
        {storeId && <Button size="small" variant={storeOnly ? 'contained' : 'outlined'} onClick={() => setStoreOnly(!storeOnly)}>{storeOnly ? 'This store' : 'All stores'}</Button>}
        <FormControlLabel control={<Switch size="small" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} />} label="Include cancelled" />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
        <Typography variant="body2"><b>{rows.length}</b> {view === 'customer' ? 'customer-order' : 'optimizer'} line(s):</Typography>
        {['pending', 'partial', 'received', 'not_supplied'].filter((k) => counts[k]).map((k) => (
          <Chip key={k} size="small" label={`${k.replace('_', ' ')} ${counts[k]}`} />
        ))}
        {(dupCount > 0 || shortCount > 0) && (
          <Chip size="small" color={onlyFlagged ? 'warning' : 'default'} variant={onlyFlagged ? 'filled' : 'outlined'} icon={<Warning />}
            label={`${dupCount} on another open PO${view === 'customer' ? ` · ${shortCount} below customer qty` : ''}${onlyFlagged ? ' (showing only these)' : ''}`}
            onClick={() => { setOnlyFlagged(!onlyFlagged); setPage(0); }} />
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '65vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <Head k="medicine" label="Medicine" />
              <Head k="pack" label="Pack" />
              {view === 'customer' && <TableCell>Customer order(s)</TableCell>}
              {view === 'optimizer' && <Head k="optimizer" label="Optimizer qty (units)" align="right" />}
              {view === 'customer' && <Head k="customer" label="Customer qty (strips → units)" align="right" />}
              <Head k="order" label="PO qty (units)" align="right" />
              <Head k="distributor" label="Distributor" />
              <Head k="po" label="PO" />
              <Head k="po_status" label="PO status" />
              <Head k="line" label="Line status" />
              <Head k="received" label="Received" align="right" />
              <Head k="sent" label="Sent" />
              <Head k="run" label="Run" />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((l) => (
              <TableRow key={l.id} hover sx={{ opacity: l.po_status === 'cancelled' ? 0.5 : 1, bgcolor: l.short_of_customer ? '#fff4f4' : undefined }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{l.medicine_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{l.manufacturer_name}{!storeOnly || !storeId ? ` · ${l.store_name}` : ''}</Typography>
                  {flags(l)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.pack_size || '—'}</TableCell>
                {view === 'customer' && (
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(l.customer_orders || []).map((o: any) => (
                        <Tooltip key={o.id} title={`${o.customer_name || '—'}${o.customer_number ? ` · ${o.customer_number}` : ''} · ${String(o.date || '').slice(0, 10)} · order status ${o.status}`}>
                          <Chip size="small" clickable color="primary" variant="outlined" onClick={() => setOrderId(o.id)}
                            label={`#${o.id}${o.customer_name ? ` · ${String(o.customer_name).slice(0, 18)}` : ''}`} />
                        </Tooltip>
                      ))}
                      {!(l.customer_orders || []).length && <Typography variant="caption" color="text.secondary">—</Typography>}
                    </Stack>
                  </TableCell>
                )}
                {view === 'optimizer' && <TableCell align="right">{num(l.optimizer_qty, 0)}</TableCell>}
                {view === 'customer' && (
                  <TableCell align="right">
                    {l.customer_qty_strips !== null && l.customer_qty_strips !== undefined
                      ? <>{num(l.customer_qty_strips, 0)} str → <b>{num(l.customer_qty_units, 0)}</b></>
                      : '—'}
                  </TableCell>
                )}
                <TableCell align="right">
                  <b>{num(l.loose_qty, 0)}</b>
                  <Typography variant="caption" display="block" color="text.secondary">{num(l.strip_qty, 0)} strips</Typography>
                </TableCell>
                <TableCell>{l.distributor_name}</TableCell>
                <TableCell><Button size="small" onClick={() => onOpenPo(l.po_id)}>{l.po_number}</Button></TableCell>
                <TableCell><StatusChip status={l.po_status} /></TableCell>
                <TableCell><StatusChip status={l.line_status} /></TableCell>
                <TableCell align="right">{num(l.received_qty, 0)}</TableCell>
                <TableCell><Typography variant="caption">{l.sent_at ? fmtDateTime(l.sent_at) : '—'}</Typography></TableCell>
                <TableCell><Typography variant="caption">{l.run_number}</Typography></TableCell>
                <TableCell>
                  {!l.reordered_to_po_id && ['pending', 'partial', 'not_supplied'].includes(l.line_status) && !['cancelled'].includes(l.po_status) && (
                    <Tooltip title={`Not available at ${l.distributor_name}? Move / re-order it from the next distributor`}>
                      <Button size="small" color="warning" variant="outlined" startIcon={<SwapHoriz />} onClick={() => setMoveLine(l)} sx={{ whiteSpace: 'nowrap' }}>
                        Not available
                      </Button>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!pageRows.length && (
              <TableRow><TableCell colSpan={14}><Typography variant="body2" color="text.secondary">{loading ? 'Loading…' : 'No lines in this range.'}</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={sorted.length} page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100, 250]} />

      <NextDistributorDialog line={moveLine} onClose={() => setMoveLine(null)} onDone={(msg) => { setMoveLine(null); setNotice(msg); load(); }} />
      <CustomerOrderDialog orderId={orderId} onClose={() => setOrderId(null)} onOpenPo={(id) => { setOrderId(null); onOpenPo(id); }} />
    </Box>
  );
};

export default PoLinesView;
